import { type SessionV2 } from "@decentriq/core";
import type JSZip from "jszip";
import { type SnackbarKey } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { type ValidationReport } from "validate-js";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomJob,
} from "features/mediaDataRoom/hooks/v3";
import { type PublishedDatasetsHashes } from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import { type AdvertiserDataReportResult as SeedAudienceDataReportResult } from "../models";

interface SeedAudienceDataReportHookPayload {
  dataRoomId: string;
  publishedDatasetsHashes: PublishedDatasetsHashes;
  session: SessionV2 | undefined;
  skip: boolean;
}

const useSeedAudienceDataReport = ({
  session,
  dataRoomId,
  publishedDatasetsHashes,
  skip,
}: SeedAudienceDataReportHookPayload): SeedAudienceDataReportResult => {
  const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
  const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];

  const transform = useCallback(
    async (
      zip: JSZip
    ): Promise<NonNullable<SeedAudienceDataReportResult["computeResults"]>> => {
      const reportFile = zip.file("validation-report.json");
      if (reportFile === null) {
        throw new Error(`${reportFile} not found in zip`);
      }
      const validationReport = JSON.parse(
        await reportFile.async("string")
      ) as ValidationReport;
      return validationReport;
    },
    []
  );
  const input = useMemo(
    () =>
      MediaDataRoomJobInput.create(
        "getAudiencesValidationReport",
        dataRoomId,
        publishedDatasetsHashes.updateDatasetsHash(
          publishedDatasetsHashes.advertiserDatasetHash
            ? {
                advertiserDatasetHash:
                  publishedDatasetsHashes.advertiserDatasetHash,
              }
            : null
        )
      ),
    [dataRoomId, publishedDatasetsHashes]
  );
  const computeJob = useMediaDataRoomJob({
    input,
    requestCreator: MediaDataRoomJobInput.emptyRequestCreator,
    session,
    skip,
    transform,
  });

  useEffect(() => {
    const error = computeJob.error;
    if (error) {
      const key = enqueueSnackbar(
        ...mapMediaDataRoomErrorToSnackbar(
          error,
          "Failed to retrieve seed audience data validation report"
        )
      );
      setErrorSnackbarId(key);
    } else {
      setErrorSnackbarId((snackbarId) => {
        if (snackbarId) {
          closeSnackbar(snackbarId);
        }
        return undefined;
      });
    }
  }, [closeSnackbar, computeJob.error, enqueueSnackbar, setErrorSnackbarId]);

  return computeJob;
};

export default useSeedAudienceDataReport;
